import React, { useMemo } from 'react';
import { Badge, Box, Button, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import {
  useGetPartMasterFilesAllQuery,
  usePostGetActivePresignedPartMasterFilesByIdQuery,
} from '@/state/api/masterFilesApi';

type Props = {
  projectId?: number | null;
  partNumber?: string | null;
  title?: string;
  minH?: string | number;
  showHeader?: boolean;
  emptyText?: string;
};

type AnyRecord = Record<string, any>;

const normalizePartToken = (value: unknown) => {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^a-z0-9]/gi, '');
};

const normalizeMasterfilesList = (payload: any): AnyRecord[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (typeof payload === 'object') {
    const values = Object.values(payload);
    if (values.every((item) => item && typeof item === 'object')) {
      return values as AnyRecord[];
    }
  }
  return [];
};

const collectMasterfileTokens = (masterfile: AnyRecord): string[] => {
  const tokens = new Set<string>();

  const push = (value: unknown) => {
    const normalized = normalizePartToken(value);
    if (normalized) tokens.add(normalized);
  };

  push(masterfile?.part_number);
  push(masterfile?.filename);
  push(masterfile?.name);
  push(masterfile?.frame);
  push(masterfile?.part?.part_number);
  push(masterfile?.part?.name);
  push(masterfile?.active_revision?.filename);
  push(masterfile?.active_revision?.file_name);
  push(masterfile?.active_revision?.file);
  push(masterfile?.active_revision?.file_url);

  if (Array.isArray(masterfile?.revisions)) {
    masterfile.revisions.forEach((revision: AnyRecord) => {
      push(revision?.filename);
      push(revision?.file_name);
      push(revision?.file);
      push(revision?.file_url);
    });
  }

  return Array.from(tokens);
};

const getMasterfileId = (masterfile: AnyRecord | null): number | null => {
  const raw = masterfile?.id ?? masterfile?.masterfile_id ?? masterfile?.master_file_id;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const extractPreviewUrl = (payload: any): string | null => {
  if (!payload) return null;

  if (typeof payload === 'string') {
    return payload.startsWith('http') ? payload : null;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const found = extractPreviewUrl(item);
      if (found) return found;
    }
    return null;
  }

  const candidateKeys = [
    'file_url',
    'url',
    'download_url',
    'presigned_url',
    'signed_url',
    'file',
  ];

  for (const key of candidateKeys) {
    if (typeof payload?.[key] === 'string' && payload[key].startsWith('http')) {
      return payload[key];
    }
  }

  if (Array.isArray(payload?.results)) {
    return extractPreviewUrl(payload.results);
  }

  if (Array.isArray(payload?.files)) {
    return extractPreviewUrl(payload.files);
  }

  if (payload?.data) {
    return extractPreviewUrl(payload.data);
  }

  if (typeof payload === 'object') {
    for (const value of Object.values(payload)) {
      const found = extractPreviewUrl(value);
      if (found) return found;
    }
  }

  return null;
};

const PartMasterfilePreview: React.FC<Props> = ({
  projectId,
  partNumber,
  title = 'Part drawing',
  minH = '420px',
  showHeader = true,
  emptyText = 'No PDF is linked to this part number yet.',
}) => {
  const normalizedPartNumber = normalizePartToken(partNumber);

  const { data: rawMasterfiles, isLoading: isLoadingMasterfiles } =
    useGetPartMasterFilesAllQuery(
      { params: { project_id: Number(projectId) } as any },
      { skip: !projectId },
    );

  const partMasterfiles = useMemo(
    () => normalizeMasterfilesList(rawMasterfiles),
    [rawMasterfiles],
  );

  const matchedMasterfile = useMemo(() => {
    if (!normalizedPartNumber || !partMasterfiles.length) return null;

    return (
      partMasterfiles.find((masterfile) => {
        const tokens = collectMasterfileTokens(masterfile);
        return tokens.includes(normalizedPartNumber);
      }) ?? null
    );
  }, [normalizedPartNumber, partMasterfiles]);

  const matchedMasterfileId = getMasterfileId(matchedMasterfile);

  const { data: presignedPayload, isLoading: isLoadingPreview } =
    usePostGetActivePresignedPartMasterFilesByIdQuery(
      { body: { masterfile_ids: matchedMasterfileId ? [matchedMasterfileId] : [] } as any },
      { skip: !matchedMasterfileId },
    );

  const previewUrl = useMemo(() => extractPreviewUrl(presignedPayload), [presignedPayload]);

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      p={4}
      w="full"
      bg="white"
      color="gray.800"
      borderColor="gray.200"
      _dark={{
        bg: '#111318',
        color: 'white',
        borderColor: '#2D3748',
      }}
    >
      {showHeader ? (
        <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
          <Stack gap={1}>
            <Text
              fontWeight="semibold"
              color="gray.900"
              _dark={{ color: 'white' }}
            >
              {title}
            </Text>
            <Text
              fontSize="sm"
              color="gray.600"
              _dark={{ color: 'gray.400' }}
            >
              {partNumber || 'No part selected'}
            </Text>
          </Stack>

          {matchedMasterfile ? (
            <Badge colorScheme="green" variant="subtle">
              PDF found
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="subtle">
              No file
            </Badge>
          )}
        </Flex>
      ) : null}

      {!projectId ? (
        <Text color="gray.600" _dark={{ color: 'gray.400' }}>
          Select a project first.
        </Text>
      ) : !partNumber ? (
        <Text color="gray.600" _dark={{ color: 'gray.400' }}>
          Select a part to preview its PDF.
        </Text>
      ) : isLoadingMasterfiles || isLoadingPreview ? (
        <Flex minH={minH} align="center" justify="center" direction="column" gap={3}>
          <Spinner />
          <Text color="gray.600" _dark={{ color: 'gray.400' }}>
            Loading part PDF…
          </Text>
        </Flex>
      ) : !matchedMasterfile || !previewUrl ? (
        <Flex minH={minH} align="center" justify="center">
          <Text
            color="gray.600"
            _dark={{ color: 'gray.400' }}
            textAlign="center"
          >
            {emptyText}
          </Text>
        </Flex>
      ) : (
        <Stack gap={3}>
          <Flex justify="flex-end">
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              as="a"
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open PDF
            </Button>
          </Flex>

          <Box
            borderWidth="1px"
            borderRadius="lg"
            overflow="hidden"
            minH={minH}
            bg="gray.50"
            borderColor="gray.200"
            _dark={{
              bg: '#0F1319',
              borderColor: '#2D3748',
            }}
          >
            <iframe
              title={`Part masterfile preview ${partNumber}`}
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                minHeight: typeof minH === 'number' ? `${minH}px` : minH,
                border: '0',
                background: 'transparent',
              }}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default PartMasterfilePreview;